import { useCallback, useEffect, useRef } from 'react';

/**
 * Full-screen image viewer (Instagram-style): tap a photo to enlarge, swipe or use the
 * arrows to move between them, tap the backdrop / press Esc / tap × to close. `index` is the
 * active photo, or null when closed.
 */
export function Lightbox({
  images,
  index,
  onClose,
  onIndex,
}: {
  images: string[];
  index: number | null;
  onClose: () => void;
  onIndex: (i: number) => void;
}) {
  const open = index !== null;
  const touchX = useRef(0);

  const go = useCallback(
    (delta: number) => {
      if (index === null) return;
      onIndex((index + delta + images.length) % images.length);
    },
    [index, images.length, onIndex],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; // lock background scroll
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, go]);

  if (!open || index === null) return null;

  const navBtn = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 44,
    height: 44,
    borderRadius: 999,
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid var(--border)',
    color: 'var(--white)',
    fontSize: 26,
    lineHeight: 1,
    backdropFilter: 'blur(6px)',
  } as const;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(0,0,0,0.94)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 12px',
        animation: 'fadeIn 0.18s ease',
      }}
    >
      {images.length > 1 && (
        <span
          style={{
            position: 'absolute',
            top: 'calc(14px + env(safe-area-inset-top))',
            left: 0,
            right: 0,
            textAlign: 'center',
            color: 'var(--text-dim)',
            fontSize: 13,
            letterSpacing: '0.1em',
          }}
        >
          {index + 1} / {images.length}
        </span>
      )}

      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        style={{
          position: 'absolute',
          top: 'calc(10px + env(safe-area-inset-top))',
          right: 14,
          width: 40,
          height: 40,
          borderRadius: 999,
          background: 'rgba(0,0,0,0.4)',
          border: '1px solid var(--border)',
          color: 'var(--white)',
          fontSize: 22,
          lineHeight: 1,
          backdropFilter: 'blur(6px)',
        }}
      >
        ×
      </button>

      <img
        src={images[index]}
        alt=""
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => {
          const t = e.touches[0];
          if (t) touchX.current = t.clientX;
        }}
        onTouchEnd={(e) => {
          const t = e.changedTouches[0];
          if (!t) return;
          const dx = t.clientX - touchX.current;
          if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
        }}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          borderRadius: 8,
          boxShadow: '0 10px 50px rgba(0,0,0,0.6)',
        }}
      />

      {images.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous photo"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            style={{ ...navBtn, left: 10 }}
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Next photo"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            style={{ ...navBtn, right: 10 }}
          >
            ›
          </button>
        </>
      )}
    </div>
  );
}
